import { Route, Routes, useNavigate } from "react-router-dom";
import { Box, Dialog, Grid, useTheme } from "@mui/material";
import { mdiCalendarMultiple, mdiCalendarTextOutline } from "@mdi/js";
import { useShallow } from "zustand/shallow";

import AppBar from "components/AppBar";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import PageLayout from "components/PageLayout";
import AddFriend from "containers/AddFriend";
import AddGroupUsers from "containers/AddGroupUsers";
import AppInfo from "containers/AppInfo";
import ConfirmResetPassword from "containers/ConfirmResetPassword";
import Event from "containers/Event";
import Friends from "containers/Friends";
import Group from "containers/Group";
import Groups from "containers/Groups";
import GroupSettings from "containers/GroupSettings";
import GroupTimeline from "containers/GroupTimeline";
import Help from "containers/Help";
import NewGroup from "containers/NewGroup";
import SetEvent from "containers/SetEvent";
import Settings from "containers/Settings";
import GroupInviteLink from "containers/GroupInviteLink";
import TimelineEventDetails from "containers/TimelineEventDetails";
import EventParticipantsVotes from "routes/with-metadata/EventParticipantsVotes";
import TimelineEventParticipantsVotes from "routes/with-metadata/TimelineEventParticipantsVotes";
import TimelineEventPendingVotes from "routes/with-metadata/TimelineEventPendingVotes";
import EventPendingVotes from "routes/with-metadata/EventPendingVotes";
import useBoundStore from "store";
import TimelineEventVehiclesVotes from "routes/with-metadata/TimelineEventVehiclesVotes";
import EventVehiclesVotes from "routes/with-metadata/EventVehiclesVotes";
import SecretSanta from "apps/SecretSanta";
import MoneyManager from "apps/MoneyManager";
import TimelineMoneyManager from "apps/MoneyManager/TimelineMoneyManager";
import RoomManager from "apps/RoomManager";
import TimelineRoomManager from "apps/RoomManager/TimelineRoomManager";
import TimelineSecretSanta from "apps/SecretSanta/TimelineSecretSanta";
import SlideUpTransition from "components/SlideUpTransition";
import styles from "./desktopPrivateRoutes.module.scss";

function DesktopPrivateRoutes() {
  const { desktopHistory, setDesktopHistory } = useBoundStore(
    useShallow((state) => ({
      desktopHistory: state.navigation.desktopHistory,
      redirectLocation: state.navigation.redirect.location,
      setDesktopHistory: state.navigation.setDesktopHistory,
    }))
  );
  const navigate = useNavigate();
  const theme = useTheme();

  const renderGrid1 = () => {
    const grid1 = desktopHistory.GRID1;

    switch (grid1[grid1.length - 1]) {
      case "groups":
        return (
          <Groups
            onCreateGroupClick={setDesktopHistory({
              goTo: { GRID1: "new-group" },
            })}
            onFriendsClick={setDesktopHistory({ goTo: { GRID1: "friends" } })}
            onGroupClick={setDesktopHistory({
              goTo: { GRID2: "group" },
              goBack: {
                GRID2: desktopHistory.GRID2.length,
                GRID3: desktopHistory.GRID3.length,
              },
            })}
            onSettingsClick={setDesktopHistory({ goTo: { GRID1: "settings" } })}
          />
        );

      case "friends":
        return (
          <Friends
            onAddFriends={setDesktopHistory({ goTo: { GRID1: "add-friends" } })}
            onBack={setDesktopHistory({ goBack: { GRID1: 1 } })}
          />
        );
      case "add-friends":
        return (
          <AddFriend
            onBack={() => {
              setDesktopHistory({ goBack: { GRID1: 1 } })();
              // If a redirect happened set url to "/" so if the user refreshes the app it won't bring him again
              // to the redirected page
              navigate("/", { replace: true });
            }}
          />
        );
      case "settings":
        return (
          <Settings
            onBack={setDesktopHistory({ goBack: { GRID1: 1 } })}
            onViewHelp={setDesktopHistory({ goTo: { GRID1: "help" } })}
          />
        );
      case "help":
        return (
          <Help
            onViewAppInfo={setDesktopHistory({ goTo: { GRID1: "app-info" } })}
            onBack={setDesktopHistory({ goBack: { GRID1: 1 } })}
          />
        );
      case "app-info":
        return <AppInfo onBack={setDesktopHistory({ goBack: { GRID1: 1 } })} />;
      case "new-group":
        return (
          <NewGroup
            onAddFriends={setDesktopHistory({ goTo: { GRID1: "add-friends" } })}
            onCreateGroup={setDesktopHistory({ goBack: { GRID1: 1 } })}
            onBack={setDesktopHistory({ goBack: { GRID1: 1 } })}
          />
        );
      default:
        return null;
    }
  };

  const renderGrid2 = () => {
    const grid2 = desktopHistory.GRID2;

    switch (grid2[grid2.length - 1]) {
      case "group-settings":
        return (
          <GroupSettings
            onAddUsers={setDesktopHistory({
              goTo: { GRID2: "add-group-users" },
            })}
            onInviteLink={setDesktopHistory({
              goTo: { GRID2: "join-via-link" },
            })}
            onLeaveGroup={setDesktopHistory({ goBack: { GRID2: 2, GRID3: 1 } })}
            onBack={setDesktopHistory({ goBack: { GRID2: 1 } })}
            onViewTimeline={setDesktopHistory({
              goTo: { GRID2: "group-timeline" },
            })}
          />
        );

      case "group":
        return (
          <Group
            backAction={false}
            hideFab={["create-event", "edit-event"].includes(desktopHistory.GRID3[desktopHistory.GRID3.length - 1])}
            onCreateEventClick={setDesktopHistory({
              goTo: { GRID3: "create-event" },
              goBack: { GRID3: desktopHistory.GRID3.length },
            })}
            onEventClick={setDesktopHistory({
              goTo: { GRID3: "event" },
              goBack: { GRID3: desktopHistory.GRID3.length },
            })}
            onGroupSettingsClick={setDesktopHistory({
              goTo: { GRID2: "group-settings" },
            })}
          />
        );
      case "add-group-users":
        return (
          <AddGroupUsers
            onAddGroupUsers={setDesktopHistory({ goBack: { GRID2: 1 } })}
            onBack={setDesktopHistory({ goBack: { GRID2: 1 } })}
          />
        );
      case "group-timeline":
        return (
          <GroupTimeline
            onBack={setDesktopHistory({ goBack: { GRID2: 1 } })}
            onEventClick={setDesktopHistory({
              goTo: { GRID2: "timeline-event-details" },
            })}
          />
        );
      case "timeline-event-details":
        return (
          <TimelineEventDetails
            onBack={setDesktopHistory({ goBack: { GRID2: 1 } })}
            onEventAppsMenuItemClick={(route) => {
              if (route === "apps/secret-santa") {
                setDesktopHistory({ goTo: { GRID2: "timeline-secret-santa" } })();
              } else if (route === "apps/money-manager") {
                setDesktopHistory({ goTo: { GRID2: "timeline-money-manager" } })();
              } else if (route === "apps/room-manager") {
                setDesktopHistory({ goTo: { GRID2: "timeline-room-manager" } })();
              }
            }}
            onViewParticipants={setDesktopHistory({
              goTo: { GRID2: "event-participants" },
            })}
            onViewPending={setDesktopHistory({
              goTo: { GRID2: "event-pending" },
            })}
            onViewVehicles={setDesktopHistory({
              goTo: { GRID2: "event-vehicles" },
            })}
          />
        );
      case "event-participants":
        return <TimelineEventParticipantsVotes onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      case "event-pending":
        return <TimelineEventPendingVotes onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      case "event-vehicles":
        return <TimelineEventVehiclesVotes onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      case "timeline-secret-santa":
        return <TimelineSecretSanta onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      case "timeline-room-manager":
        return <TimelineRoomManager onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      case "join-via-link":
        return <GroupInviteLink onBack={setDesktopHistory({ goBack: { GRID2: 1 } })} />;
      default:
        return (
          <PageLayout appBar={<AppBar title='Events' backAction={false} />}>
            <DataIconPlaceholder
              text='Choose a group to either create new events or view existing ones.'
              icon={mdiCalendarMultiple}
            />
          </PageLayout>
        );
    }
  };

  const renderGrid3 = () => {
    const grid3 = desktopHistory.GRID3;

    switch (grid3[grid3.length - 1]) {
      case "event":
        return (
          <Event
            backAction={false}
            onArchiveEvent={setDesktopHistory({ goBack: { GRID3: 1 } })}
            onDeleteEvent={setDesktopHistory({ goBack: { GRID3: 1 } })}
            onEditEvent={setDesktopHistory({ goTo: { GRID3: "edit-event" } })}
            onEventAppsMenuItemClick={(route) => {
              if (route === "apps/secret-santa") {
                setDesktopHistory({ goTo: { GRID3: "secret-santa" } })();
              } else if (route === "apps/money-manager") {
                setDesktopHistory({ goTo: { GRID3: "money-manager" } })();
              } else if (route === "apps/room-manager") {
                setDesktopHistory({ goTo: { GRID3: "room-manager" } })();
              }
            }}
            onViewParticipants={setDesktopHistory({
              goTo: { GRID3: "event-participants" },
            })}
            onViewPending={setDesktopHistory({
              goTo: { GRID3: "event-pending" },
            })}
            onViewVehicles={setDesktopHistory({
              goTo: { GRID3: "event-vehicles" },
            })}
          />
        );
      case "create-event":
        return (
          <SetEvent
            onBack={setDesktopHistory({ goBack: { GRID3: 1 } })}
            onSetEvent={setDesktopHistory({ goBack: { GRID3: 1 } })}
          />
        );
      case "edit-event":
        return (
          <SetEvent
            onBack={setDesktopHistory({ goBack: { GRID3: 1 } })}
            onSetEvent={setDesktopHistory({ goBack: { GRID3: 1 } })}
          />
        );
      case "event-participants":
        return <EventParticipantsVotes onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      case "event-pending":
        return <EventPendingVotes onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      case "event-vehicles":
        return <EventVehiclesVotes onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      case "secret-santa":
        return <SecretSanta onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      case "money-manager":
        return <MoneyManager onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      case "room-manager":
        return <RoomManager onBack={setDesktopHistory({ goBack: { GRID3: 1 } })} />;
      default:
        return (
          <PageLayout appBar={<AppBar title='Details' backAction={false} />}>
            <DataIconPlaceholder text='Choose an event to access additional details.' icon={mdiCalendarTextOutline} />
          </PageLayout>
        );
    }
  };

  const isMoneyManagerOpen = desktopHistory.GRID3[desktopHistory.GRID3.length - 1] === "money-manager";
  const handleMoneyManagerClose = setDesktopHistory({ goBack: { GRID3: 1 } });

  const isTimelineMoneyManagerOpen = desktopHistory.GRID2[desktopHistory.GRID2.length - 1] === "timeline-money-manager";
  const handleTimelineMoneyManagerClose = setDesktopHistory({ goBack: { GRID2: 1 } });

  const renderGrids = () => (
    <>
      <div
        className={styles.background}
        style={{
          backgroundColor: theme.palette.mode === "light" ? "white" : "black",
        }}
      />
      <Grid
        className={styles.gridContainer}
        container
        spacing={2}
        style={{ backgroundColor: theme.palette.primary.main }}
      >
        <Grid item xs={4}>
          {renderGrid1()}
        </Grid>
        <Grid item xs={4}>
          {renderGrid2()}
        </Grid>
        <Grid item xs={4}>
          {renderGrid3()}
        </Grid>
      </Grid>
      <Dialog
        maxWidth='md'
        fullScreen
        open={isMoneyManagerOpen}
        TransitionComponent={SlideUpTransition}
        PaperProps={{ sx: { overflow: "hidden", borderRadius: 0 } }}
      >
        <Box sx={{ height: "100%", "& > .MuiBox-root": { borderRadius: 0, height: "100%" } }}>
          <MoneyManager onBack={handleMoneyManagerClose} />
        </Box>
      </Dialog>
      <Dialog
        maxWidth='md'
        fullScreen
        open={isTimelineMoneyManagerOpen}
        TransitionComponent={SlideUpTransition}
        PaperProps={{ sx: { overflow: "hidden", borderRadius: 0 } }}
      >
        <Box sx={{ height: "100%", "& > .MuiBox-root": { borderRadius: 0, height: "100%" } }}>
          <TimelineMoneyManager onBack={handleTimelineMoneyManagerClose} />
        </Box>
      </Dialog>
    </>
  );

  return (
    <Routes>
      <Route path='/confirm-reset-password' element={<ConfirmResetPassword />} />
      <Route path='/*' element={renderGrids()} />
    </Routes>
  );
}

export default DesktopPrivateRoutes;
